import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import React, { useRef, useState, useEffect } from 'react';
import _ from 'lodash';
import PropTypes from 'prop-types';
import i18n from 'i18next';
import moment from 'moment';
import cx from 'classnames';
import { useSelector, useDispatch } from 'react-redux';
import Checkbox from 'ui/components/checkbox';
import DataTable from '../ui/table';
import ModalDialog from '../ui/modal-dialog';
import DropDown from '../ui/react-ui/dropdown';
import Textarea from '../ui/textarea';
import Form from '../ui/form';
import Input from '../ui/input';
import DatePicker from '../ui/react-ui/date-picker';
import Validator from '../../utils/validator';
import { createEventAPI, editEventAPI } from '../../api/eventAPI';
import { clearDialogInfo } from '../../redux/event-management';
var t = i18n.getFixedT(null, 'event-management');
var gt = i18n.getFixedT(null, 'app');
var et = i18n.getFixedT(null, 'errors');
var initEventData = {
  id: '',
  source: '',
  typeId: '',
  // current harbor is only TWKHH. Call harbor api to select other harbor
  harborId: 'TWKHH',
  harborName: '',
  authority: '',
  latitude: '',
  longitude: '',
  beginTime: moment().format('YYYY-MM-DD HH:mm'),
  endTime: moment().add(1, 'days').format('YYYY-MM-DD HH:mm'),
  location: '',
  description: '',
  isPublished: false
};
var EventTypeDraftEdit = /*#__PURE__*/React.memo(function (props) {
  // redux
  var _useSelector = useSelector(function (state) {
    return state.toJS();
  }),
      contextRoot = _useSelector.envCfg.contextRoot,
      _useSelector$eventMan = _useSelector.eventManagement,
      _useSelector$eventMan2 = _useSelector$eventMan.dialogInfo,
      mode = _useSelector$eventMan2.mode,
      data = _useSelector$eventMan2.data,
      eventTypeList = _useSelector$eventMan.eventTypeList;

  var dispatch = useDispatch();

  var _useState = useState(),
      _useState2 = _slicedToArray(_useState, 2),
      error = _useState2[0],
      setError = _useState2[1];

  var _useState3 = useState(initEventData),
      _useState4 = _slicedToArray(_useState3, 2),
      formData = _useState4[0],
      setFormData = _useState4[1];

  var id = props.id,
      onClose = props.onClose;
  var form_ref = useRef(null);

  var _API = useRef(null);

  var handleClose = function handleClose() {
    dispatch(clearDialogInfo());
    setFormData(initEventData);
    setError(null);
  };

  var handleChange = function handleChange(value, path) {
    var newData = _.cloneDeep(formData);

    if (value && path === 'beginTime') {
      if (moment(value).valueOf() > moment(newData.endTime).valueOf()) _.set(newData, 'endTime', value);
    }

    if (value && path === 'endTime') {
      if (moment(value).valueOf() < moment(newData.beginTime).valueOf()) _.set(newData, 'beginTime', value);
    }

    _.set(newData, path, value);

    setFormData(newData);
  };

  var handleConfirm = function handleConfirm() {
    var newFormData = _.cloneDeep(formData);

    if (!newFormData.typeId) return setError(t('txt-select-eventType-required')); // let fillOutRequired = Validator(['description', 'location', 'longitude', 'latitude'], formData)

    var fillOutRequired = Validator(['description', 'location'], formData);
    if (fillOutRequired.length > 0) return setError(et('txt-fill-out-required'));
    if (_.isEmpty(newFormData.beginTime) || _.isEmpty(newFormData.endTime)) return setError(t('txt-time-required'));

    _.set(newFormData, 'beginTime', newFormData.beginTime.length === 16 ? newFormData.beginTime + ':00' : newFormData.beginTime);

    _.set(newFormData, 'endTime', newFormData.endTime.length === 16 ? newFormData.endTime + ':00' : newFormData.endTime);

    if (mode === 'eventDraft.create') _.set(newFormData, 'source', 'EVMS');

    _API.current(contextRoot, false, newFormData).then(function (result) {
      setFormData(initEventData);
      setError(null);
      onClose();
    }).catch(function (error) {
      setError(error.message);
    });
  };

  useEffect(function () {
    if (mode.indexOf('eventDraft') > -1) {
      // confirm edit or create api
      _API.current = mode.indexOf('edit') > -1 ? editEventAPI : createEventAPI;
    }

    if (_.isEmpty(mode) || mode.indexOf('eventDraft.edit') < 0) return null;
    setFormData(data);
  }, [mode]);
  if (mode.indexOf('eventDraft') < 0 || mode.indexOf('edit') > -1 && _.isEmpty(formData.id)) return null;
  var actions = {};

  _.set(actions, 'cancel', {
    text: gt('global.lbl-answer-cancel'),
    handler: handleClose
  });

  _.set(actions, 'confirm', {
    text: gt('global.lbl-answer-ok'),
    className: 'button-success',
    // disabled: !formValid,
    handler: handleConfirm
  });

  var typeList = _.map(eventTypeList, function (el) {
    return {
      value: el.id,
      text: el.name
    };
  });

  var fields = {};

  _.set(fields, 'typeId', {
    label: t('lbl-event-type'),
    formatter: function formatter(val, row) {
      return /*#__PURE__*/React.createElement(DropDown, {
        className: "eventType-dropdown",
        defaultText: gt('global.lbl-select'),
        list: typeList,
        onChange: function onChange(val) {
          return handleChange(val, 'typeId');
        },
        value: val ? val : ''
      });
    }
  });

  _.set(fields, 'description', {
    label: t('lbl-description'),
    editor: null,
    formatter: function formatter(val, row) {
      return /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(Textarea, {
        validate: {
          required: true
        },
        onChange: function onChange(val) {
          return handleChange(val, 'description');
        },
        value: val
      }));
    }
  });

  _.set(fields, 'location', {
    label: t('dlg-place'),
    editor: null,
    formatter: function formatter(val, row) {
      return /*#__PURE__*/React.createElement(Input, {
        value: val ? val : '',
        validate: {
          required: true
        },
        onChange: function onChange(val) {
          return handleChange(val, 'location');
        }
      });
    }
  });

  _.set(fields, 'longitude', {
    label: t('dlg-longitude'),
    formatter: function formatter(val, row) {
      return /*#__PURE__*/React.createElement(Input, {
        value: val ? val : '',
        validate: {
          // required: true,
          pattern: /^[-+]?(180(\.0+)?|((1[0-7]\d)|([1-9]?\d))(\.\d+)?)$/,
          t: function t(code) {
            return et("txt-".concat(code));
          }
        },
        onChange: function onChange(val) {
          return handleChange(val, 'longitude');
        }
      });
    }
  });

  _.set(fields, 'latitude', {
    label: t('dlg-latitude'),
    formatter: function formatter(val, row) {
      return /*#__PURE__*/React.createElement(Input, {
        value: val ? val : '',
        validate: {
          // required: true,
          pattern: /^\s*[-+]?([1-8]?\d(\.\d+)?|90(\.0+)?)$/,
          t: function t(code) {
            return et("txt-".concat(code));
          }
        },
        onChange: function onChange(val) {
          return handleChange(val, 'latitude');
        }
      });
    }
  });

  _.set(fields, 'beginTime', {
    label: t('lbl-start-time'),
    formatter: function formatter(val, row) {
      return /*#__PURE__*/React.createElement(DatePicker, {
        id: "datetime",
        onChange: function onChange(val) {
          return handleChange(val, 'beginTime');
        },
        enableTime: true,
        value: val // t={(code, params) => {
        //     if (code === 'missing')
        //         return `Please input date`
        //     else {
        //         return `Invalid date format. Should be ${params.pattern}`
        //     }
        // }}

      });
    }
  });

  _.set(fields, 'endTime', {
    label: t('lbl-end-time'),
    formatter: function formatter(val, row) {
      return /*#__PURE__*/React.createElement(DatePicker, {
        id: "datetime",
        onChange: function onChange(val) {
          return handleChange(val, 'endTime');
        },
        enableTime: true,
        value: val // t={(code, params) => {
        //     if (code === 'missing')
        //         return `Please input date`
        //     else {
        //         return `Invalid date format. Should be ${params.pattern}`
        //     }
        // }}

      });
    }
  });

  return /*#__PURE__*/React.createElement(ModalDialog, {
    id: id,
    title: mode.indexOf('edit') > 0 ? t('dlg-edit-event') : t('dlg-create-event'),
    global: true,
    info: error,
    infoClassName: cx({
      'c-error': error
    }),
    actions: actions,
    closeAction: "cancel"
  }, /*#__PURE__*/React.createElement(Form, {
    id: "".concat(id, "-form-left"),
    ref: form_ref,
    className: "pure-form-aligned",
    fieldClassName: "pure-control-group",
    fields: fields,
    onChange: handleChange,
    value: formData
  }));
});
EventTypeDraftEdit.defaultProps = {
  id: 'g-eventType-draft-edit'
};
EventTypeDraftEdit.propTypes = {
  id: PropTypes.string
};
export default EventTypeDraftEdit;